import { useState, useEffect } from 'react';
import { productService } from '@services/api';

export const useProducts = (filters = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await productService.getAll(filters);
        setProducts(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        // Use mock data for demo
        setProducts(getMockProducts());
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [JSON.stringify(filters)]);

  const createProduct = async (productData) => {
    try {
      const newProduct = await productService.create(productData);
      setProducts((prev) => [...prev, newProduct]);
      return newProduct;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  const updateProduct = async (id, productData) => {
    try {
      const updated = await productService.update(id, productData);
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? updated : p))
      );
      return updated;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  const deleteProduct = async (id) => {
    try {
      await productService.delete(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      throw new Error(err.message);
    }
  };

  return {
    products,
    loading,
    error,
    createProduct,
    updateProduct,
    deleteProduct,
  };
};

// Mock data for demo
const getMockProducts = () => [
  {
    id: '1',
    sku: 'CAM-HD-100',
    title: 'HD Security Camera',
    description: 'Professional grade HD security camera with night vision and motion detection. Features 1080p resolution, weatherproof design, and mobile app integration.',
    price: 199.99,
    stock: 45,
    stockStatus: 'in-stock',
    category: 'cameras',
    image: '/images/1.jpg',
    images: ['/images/1.jpg', '/images/2.jpg'],
    features: ['1080p HD Resolution', 'Night Vision', 'Motion Detection', 'Weather Resistant', 'Mobile App'],
    specs: {
      'Resolution': '1920x1080',
      'Field of View': '110°',
      'Night Vision': 'Up to 30m',
      'Connectivity': 'Wi-Fi, Ethernet',
      'Power': '12V DC',
    },
  },
  {
    id: '2',
    sku: 'LOCK-PRO-01',
    title: 'Smart Lock Pro',
    description: 'Advanced smart lock with biometric authentication, keypad entry, and remote access via smartphone.',
    price: 299.99,
    stock: 12,
    stockStatus: 'low-stock',
    category: 'smart-locks',
    image: '/images/4.jpg',
    images: ['/images/4.jpg', '/images/3.jpg'],
    features: ['Fingerprint Scanner', 'Keypad Entry', 'Smartphone Control', 'Auto-Lock', 'Battery Backup'],
    specs: {
      'Authentication': 'Fingerprint, PIN, App',
      'Battery Life': 'Up to 12 months',
      'Connectivity': 'Bluetooth, Wi-Fi',
      'Material': 'Zinc Alloy',
      'Compatibility': 'iOS & Android',
    },
  },
  {
    id: '3',
    sku: 'ALM-BAS-50',
    title: 'Alarm System Basic',
    description: 'Comprehensive alarm system for home security with door/window sensors, motion detectors, and 24/7 monitoring.',
    price: 349.99,
    stock: 28,
    stockStatus: 'in-stock',
    category: 'alarms',
    image: '/images/2.jpg',
    images: ['/images/2.jpg', '/images/3.jpg'],
    features: ['Door/Window Sensors', 'Motion Detectors', '24/7 Monitoring', 'Mobile Alerts', 'Backup Battery'],
    specs: {
      'Sensors Included': '4 Door/Window, 2 Motion',
      'Siren Volume': '120dB',
      'Connectivity': 'Wi-Fi, Cellular Backup',
      'Power': 'AC with Battery Backup',
      'Monitoring': 'Optional Professional',
    },
  },
  {
    id: '4',
    sku: 'ACC-CON-200',
    title: 'Access Control System',
    description: 'Professional access control system with RFID card reader and up to 1000 user capacity.',
    price: 599.99,
    stock: 15,
    stockStatus: 'in-stock',
    category: 'access-control',
    image: '/images/3.jpg',
    images: ['/images/3.jpg', '/images/4.jpg'],
    features: ['RFID Card Reader', '1000 User Capacity', 'Time Attendance', 'Remote Management', 'Access Logs'],
    specs: {
      'User Capacity': '1000',
      'Card Type': 'RFID 125KHz',
      'Communication': 'TCP/IP, RS485',
      'Operating Voltage': '12V DC',
      'Storage': '100,000 records',
    },
  },
  {
    id: '5',
    sku: 'CAM-PT-300',
    title: 'PTZ Security Camera',
    description: 'Pan-Tilt-Zoom camera with 360° rotation and 4x optical zoom for comprehensive surveillance.',
    price: 449.99,
    stock: 8,
    stockStatus: 'low-stock',
    category: 'cameras',
    image: '/images/5.jpg',
    images: ['/images/5.jpg', '/images/1.jpg'],
    features: ['360° Pan', 'Optical Zoom 4x', '2MP Resolution', 'Two-Way Audio', 'Motion Tracking'],
    specs: {
      'Resolution': '1920x1080',
      'Pan Range': '360°',
      'Tilt Range': '90°',
      'Zoom': '4x Optical',
      'Night Vision': 'Up to 40m',
    },
  },
  {
    id: '6',
    sku: 'DOOR-INT-100',
    title: 'Video Doorbell',
    description: 'Smart video doorbell with HD camera, two-way audio, and motion detection.',
    price: 179.99,
    stock: 32,
    stockStatus: 'in-stock',
    category: 'cameras',
    image: '/images/6.jpg',
    images: ['/images/6.jpg', '/images/7.jpg'],
    features: ['HD Video', 'Two-Way Audio', 'Motion Alerts', 'Cloud Storage', 'Night Vision'],
    specs: {
      'Video Quality': '1080p HD',
      'Field of View': '160°',
      'Power': 'Battery or Wired',
      'Storage': 'Cloud & Local',
      'App Support': 'iOS & Android',
    },
  },
  {
    id: '7',
    sku: 'SEN-MOT-50',
    title: 'Motion Sensor Pack',
    description: 'Set of wireless motion sensors for comprehensive area coverage.',
    price: 89.99,
    stock: 50,
    stockStatus: 'in-stock',
    category: 'alarms',
    image: '/images/7.jpg',
    images: ['/images/7.jpg', '/images/8.jpg'],
    features: ['Wireless', 'Pet Immune', 'Wide Detection Angle', 'Long Battery Life', 'Easy Installation'],
    specs: {
      'Detection Range': 'Up to 12m',
      'Detection Angle': '110°',
      'Battery Life': 'Up to 2 years',
      'Pet Immunity': 'Up to 25kg',
      'Frequency': '433MHz',
    },
  },
  {
    id: '8',
    sku: 'LOCK-PAD-20',
    title: 'Smart Padlock',
    description: 'Keyless smart padlock with Bluetooth connectivity and weatherproof design.',
    price: 79.99,
    stock: 40,
    stockStatus: 'in-stock',
    category: 'smart-locks',
    image: '/images/8.jpg',
    images: ['/images/8.jpg', '/images/4.jpg'],
    features: ['Keyless Entry', 'Bluetooth', 'Weatherproof IP67', 'Rechargeable', 'Share Access'],
    specs: {
      'Material': 'Hardened Steel',
      'Connectivity': 'Bluetooth 5.0',
      'Battery': 'Rechargeable Li-ion',
      'Waterproof': 'IP67',
      'Unlocking Methods': 'App, Fingerprint',
    },
  },
];

